"use client";

import React from "react";
import { Facebook, Twitter, Linkedin, Instagram, MapPin, Phone, Mail, House, Scale, ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
const quickLinks = [{
  label: "Property Management Services",
  path: "/ServicesPage"
}, {
  label: "Available Properties",
  path: "/AvailablePage"
}, {
  label: "Real Estate Investment",
  path: "/InvestorsPage"
}, {
  label: "Contact Us",
  path: "/ContactPage"
}];
const socialLinks = [{
  icon: Facebook,
  href: "#",
  label: "Facebook"
}, {
  icon: Twitter,
  href: "#",
  label: "Twitter"
}, {
  icon: Linkedin,
  href: "#",
  label: "LinkedIn"
}, {
  icon: Instagram,
  href: "#",
  label: "Instagram"
}];
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-3 bg-primary/20 border border-primary/30 rounded-md">
        <p className="text-sm text-primary-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-3">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <div className="flex gap-2">
              <FormControl>
                <Input type="email" placeholder="Enter your email" className="bg-background/50 border-border/50 focus-visible:ring-primary" {...field} />
              </FormControl>
              <Button type="submit" disabled={isSubmitting} size="icon" className="shrink-0">
                {isSubmitting ? <Loader className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
                <span className="sr-only">Subscribe</span>
              </Button>
            </div>
            <FormMessage className="text-xs" />
          </FormItem>} />
      {submitError && <p className="text-xs text-destructive">{submitError}</p>}
    </Form>;
}
export default function InvestKcFooter() {
  return <footer id="invest-kc-footer" className="dark bg-background text-foreground pt-16 pb-8 px-4 md:px-8 border-t border-border/20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          
          {}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/image_k54p87xk.png" alt="Invest KC Property Management" className="h-7 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">Managing residential and commercial properties—homes, duplexes, small multifamily, and select office and retail—throughout the Kansas City metro.</p>
          </div>

          {}
          <div>
            <h3 className="font-semibold text-lg mb-6 tracking-tight !text-[rgba(234,228,216,1)]">Quick Links</h3>
            <ul className="space-y-3 !text-[rgba(207,197,179,1)] !bg-[rgba(184,156,106,0)]">
              {quickLinks.map((link, i) => <li key={link.path} data-index={i}>
                  <Link to={link.path} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                    <span className="h-1 w-1 rounded-full bg-primary/50 group-hover:bg-primary transition-colors" />
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div>
            <h3 className="font-semibold text-lg mb-6 tracking-tight !text-[rgba(234,228,216,1)]">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                <span>5545 N Oak Trfy, Suite 12c<br />Kansas City, MO 64118</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone className="size-5 text-primary shrink-0" />
                <Link to="tel:8165551234" className="hover:text-foreground transition-colors">(816) 600-7900<br /></Link>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="size-5 text-primary shrink-0" />
                <Link to="mailto:info@investkcpm.com" className="hover:text-foreground transition-colors">
                  info@investkcpm.com
                </Link>
              </li>
            </ul>
          </div>

          {}
          <div>
            <h3 className="font-semibold text-lg mb-6 tracking-tight !text-[rgba(234,228,216,1)]">Market Insights</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Subscribe to our newsletter for Kansas City rental market updates.
            </p>
            
            <FormProvider formId="invest-kc-newsletter-footer" sectionName="footer" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="Thanks for subscribing!">
              <NewsletterForm />
            </FormProvider>

            <div className="mt-8">
              <h4 className="text-sm font-medium mb-3 text-foreground/80">Follow Us</h4>
              <div className="flex gap-3">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={social.label} to={social.href} className="size-9 rounded-md bg-muted/50 flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-200 inline-flex" data-index={i}>
                      <Icon className="size-4" />
                      <span className="sr-only">{social.label}</span>
                    </Link>;
              })}
              </div>
            </div>
          </div>

        </div>

        <Separator className="bg-border/30 mb-8" />

        {}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
          <p>
            &copy; {new Date().getFullYear()} Invest KC Property Management. All rights reserved.
          </p>
          
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2" title="Equal Housing Opportunity">
              <House className="size-4" />
              <span className="sr-only">Equal Housing Opportunity</span>
            </div>
            <div className="flex items-center gap-2" title="Fair Housing Act">
              <Scale className="size-4" />
              <span className="sr-only">Fair Housing Act</span>
            </div>
            <div className="flex gap-4">
              <Link to="#" className="hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link to="#" className="hover:text-foreground transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}